<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';
// GK-HUB: Replaced IspLineChart (SVG) with IspChartjsLine (Chart.js smooth curves + gradient + goal line)
import IspChartjsLine from './components/IspChartjsLine.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
import { formatTime, formatTrend } from './composables/ispFormatters';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';

const store = useStore();
const { t } = useI18n();

const {
  data: fcr,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filteredAgents, // GK-HUB: Agents list (only role=agent)
} = useIspReport({
  getter: 'ispReports/getFcr',
  action: 'ispReports/fetchFcr',
  filters: { agentId: '', teamId: '' },
  initActions: ['agents/get'],
});

// GK-HUB: Team filter (replaces department filter for FCR)
const teams = computed(() => store.getters['teams/getTeams']);
const selectedTeamId = ref('');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const userRole = computed(() =>
  getUserRole(currentUser.value, currentAccountId.value)
);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(userRole.value)
);

onMounted(() => {
  store.dispatch('teams/get');
});

const onTeamChange = e => {
  selectedTeamId.value = e.target.value;
  onFilterChange('teamId', e);
};
// GK-HUB: end team filter

const summary = computed(() => fcr.value.data?.summary || {});

const getFcrColor = rate => {
  if (rate >= 80) return 'teal';
  if (rate >= 60) return 'blue';
  if (rate >= 40) return 'warning';
  return 'error';
};

const barItems = computed(() =>
  (fcr.value.data?.por_motivo || []).map(m => ({
    label: m.motivo,
    value: `${m.fcr_rate}%`,
    displayValue: `${m.fcr_rate}%`,
    percentage: m.fcr_rate,
    color: getFcrColor(m.fcr_rate),
  }))
);

const agentColumns = [
  { key: 'agent_name', label: t('ISP_REPORTS.FCR.AGENT') },
  {
    key: 'conversas',
    label: t('ISP_REPORTS.FCR.CONVERSATIONS'),
    align: 'right',
    type: 'mono',
  },
  { key: 'fcr_rate', label: 'FCR', align: 'right', type: 'badge' },
  {
    key: 'tma',
    label: t('ISP_REPORTS.MOTIVOS.TMA'),
    align: 'right',
    type: 'mono',
  },
  {
    key: 'trend',
    label: t('ISP_REPORTS.FCR.TREND'),
    align: 'right',
    type: 'trend',
  },
];

const agentRows = computed(() =>
  (fcr.value.data?.por_agente || []).map(a => ({
    agent_name: a.agent_name,
    conversas: a.conversas,
    fcr_rate: `${a.fcr_rate}%`,
    tma: formatTime(a.tma_seconds),
    trend: formatTrend(a.trend),
  }))
);

// GK-HUB: Chart.js series for evolution (replaces IspLineChart SVG)
const chartSeries = computed(() => {
  const evolucao = fcr.value.data?.evolucao || [];
  if (!evolucao.length) return [];
  return [
    {
      name: 'FCR',
      color: '#5DADE2',
      data: evolucao.map(e => ({
        value: e.fcr_rate,
        label: new Date(e.timestamp * 1000).toLocaleDateString('pt-BR', {
          month: 'short',
        }),
      })),
    },
  ];
});

const onExportCsv = () => {
  const rows = fcr.value.data?.por_agente || [];
  exportCsv(
    rows,
    'Agente,Conversas,FCR (%),TMA (s)\n',
    r => `"${r.agent_name}",${r.conversas},${r.fcr_rate},${r.tma_seconds || 0}`,
    'fcr-report.csv'
  );
};
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('ISP_REPORTS.FCR.HEADER') }}
        </h1>
        <p class="text-[13px] text-n-slate-10 mt-0.5">
          {{ t('ISP_REPORTS.FCR.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <div class="isp-select-wrap">
        <select class="isp-select" @change="e => onFilterChange('agentId', e)">
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_AGENTS') }}
          </option>
          <!-- GK-HUB: filteredAgents shows only role=agent -->
          <option
            v-for="agent in filteredAgents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: Team filter (replaces department filter for FCR) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          v-model="selectedTeamId"
          class="isp-select"
          @change="onTeamChange"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
      <button
        class="btn-export border-n-slate-7 text-n-slate-10 hover:border-n-slate-9 hover:text-n-slate-12"
        @click="onExportCsv"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading (initial only) -->
    <woot-loading-state
      v-if="fcr.isFetching && !fcr.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error (initial only) -->
    <div
      v-else-if="fcr.error && !fcr.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="fcr.data">
      <!-- Summary Cards -->
      <div
        class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-6"
      >
        <IspMetricCard
          :label="t('ISP_REPORTS.FCR.FCR_GERAL')"
          :value="`${summary.fcr_geral || 0}%`"
          :trend="`${t('ISP_REPORTS.FCR.VS_PREVIOUS')}`"
          trend-direction="up"
          value-color="text-n-teal-11"
          :loading="fcr.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.FCR.BOT_RESOLUTION')"
          :value="`${summary.bot_resolution_rate || 0}%`"
          :trend="`${t('ISP_REPORTS.FCR.VS_PREVIOUS')}`"
          trend-direction="up"
          value-color="text-n-blue-11"
          :loading="fcr.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.FCR.RETORNO')"
          :value="`${summary.retorno_rate || 0}%`"
          :trend="`${t('ISP_REPORTS.FCR.VS_PREVIOUS')}`"
          trend-direction="down"
          value-color="text-amber-500"
          :loading="fcr.isFetching"
        />
      </div>

      <!-- GK-HUB: 60/40 layout for charts (bar + evolution side by side) -->
      <div class="fcr-charts-grid">
        <!-- FCR by Reason (60%) -->
        <IspBarChart
          :title="t('ISP_REPORTS.FCR.POR_MOTIVO')"
          :subtitle="t('ISP_REPORTS.FCR.POR_MOTIVO_SUBTITLE')"
          :items="barItems"
        />

        <!-- Evolution (40%) — Chart.js with goal line, legend hidden per user request -->
        <IspChartjsLine
          v-if="chartSeries.length"
          :title="t('ISP_REPORTS.FCR.EVOLUCAO')"
          :series="chartSeries"
          :goal-line="80"
          :goal-label="`${t('ISP_REPORTS.FCR.META')} 80%`"
          :show-legend="false"
          :height="260"
        />
      </div>
      <!-- GK-HUB: end 60/40 layout -->

      <!-- FCR by Agent (scrollable if many agents) -->
      <div class="fcr-agent-table">
        <IspDataTable
          :title="t('ISP_REPORTS.FCR.POR_AGENTE')"
          :columns="agentColumns"
          :rows="agentRows"
        />
      </div>
    </template>

    <!-- Empty State -->
    <div
      v-else-if="!fcr.isFetching && !fcr.error"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.EMPTY_FCR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.EMPTY_FCR_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ── Filter select wrapper (same pattern as IspMotivos/IspConversas) ─ */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode default) ──────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  /* Light mode colors */
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Export button (from isp-filters.css) ───────────────────────────── */
.btn-export {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border-width: 1.5px;
  border-style: solid;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

/* ── 60/40 Charts grid — align-items stretch = same height ─────────── */
.fcr-charts-grid {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 20px;
  margin-bottom: 24px;
  align-items: stretch;
}
/* Make children stretch to fill grid cell height */
.fcr-charts-grid > :deep(*) {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
/* Chart.js card expands to fill available space */
.fcr-charts-grid > :deep(*) > div:last-child {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.fcr-charts-grid > :deep(*) > div:last-child > div {
  flex: 1;
}

/* ── Agent table: scroll when many rows ────────────────────────────── */
.fcr-agent-table :deep(.overflow-x-auto) {
  max-height: 480px;
  overflow-y: auto;
}
/* Sticky header inside scroll */
.fcr-agent-table :deep(thead) {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* ── DatePicker calendar fix ───────────────────────────────────────── */
:deep(.date-picker) {
  position: relative;
  z-index: 20;
}
:deep(.date-picker .datepicker--container),
:deep(.date-picker [class*='absolute']) {
  z-index: 50 !important;
}
:deep(.daterange-picker--container),
:deep(.datepicker--container) {
  z-index: 50 !important;
}

@media (max-width: 768px) {
  .fcr-charts-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
/* ── Filter select: dark mode (IspFcr scope) ─────────────────────── */
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
